<template>
    <v-text-field
        ref="field"
        :value="text"
        :label="label"
        :dense="dense"
        :outlined="outlined"
        :disabled="disabled"
        :clearable="clearable"
        :prepend-inner-icon="prependInnerIcon"
        :error-messages="errorMessages"
        :hide-details="hideDetails"
        placeholder="hh:mm"
        maxlength="5"
        inputmode="numeric"
        autocomplete="off"
        @input="onInput"
        @blur="onBlur"
        @keydown.enter="onBlur"
    >
        <template v-slot:append>
            <v-menu
                v-model="menu"
                :close-on-content-click="false"
                :disabled="disabled"
                transition="scale-transition"
                offset-y
                left
                min-width="auto"
            >
                <template v-slot:activator="{ on, attrs }">
                    <v-icon v-bind="attrs" v-on="on" :disabled="disabled" style="cursor: pointer">mdi-clock-time-four-outline</v-icon>
                </template>
                <v-time-picker :value="timeValue" format="24hr" no-title @input="onPick" @click:minute="menu = false" />
            </v-menu>
        </template>
    </v-text-field>
</template>

<script setup>
import { ref, computed, watch } from 'vue'

// Campo de horario em que o usuario pode digitar (mascara hh:mm, 24h) ou escolher pelo
// relogio (icone). O valor (v-model) e sempre "HH:mm" ou null.
const props = defineProps({
    value: { type: String, default: null },
    label: { type: String, default: null },
    errorMessages: { type: [Array, String], default: undefined },
    prependInnerIcon: { type: String, default: undefined },
    dense: { type: Boolean, default: false },
    outlined: { type: Boolean, default: false },
    disabled: { type: Boolean, default: false },
    clearable: { type: Boolean, default: false },
    hideDetails: { type: [Boolean, String], default: false },
})

const emit = defineEmits(['input'])

const field = ref(null)
const menu = ref(false)

// A API pode devolver "08:00" ou "08:00:00" - so interessa hora e minuto.
function toTime(value) {
    const match = /^(\d{1,2}):(\d{2})/.exec(String(value || ''))
    if (!match) return null

    const time = `${match[1].padStart(2, '0')}:${match[2]}`
    return validTime(time) ? time : null
}

function validTime(text) {
    const match = /^(\d{2}):(\d{2})$/.exec(text)
    return !!match && Number(match[1]) <= 23 && Number(match[2]) <= 59
}

function mask(raw) {
    const digits = String(raw || '').replace(/\D/g, '').slice(0, 4)

    return digits.length <= 2 ? digits : `${digits.slice(0, 2)}:${digits.slice(2)}`
}

const text = ref(toTime(props.value) || '')
const timeValue = computed(() => toTime(props.value))

watch(() => props.value, (value) => {
    // Nao apaga o que o usuario esta digitando quando o valor emitido volta do pai.
    const current = validTime(text.value) ? text.value : null
    if (toTime(value) !== current) {
        text.value = toTime(value) || ''
    }
})

function setText(value) {
    text.value = value

    // Se a mascara nao mudou o texto (ex.: letra digitada), o Vuetify nao re-renderiza
    // o input - forca o valor mascarado no campo.
    if (field.value && field.value.lazyValue !== value) {
        field.value.lazyValue = value
    }
}

function onInput(raw) {
    const masked = mask(raw)
    setText(masked)

    if (masked === '') {
        emit('input', null)
        return
    }

    if (validTime(masked)) emit('input', masked)
}

// Texto incompleto ou horario invalido ao sair do campo: volta para o ultimo horario valido.
function onBlur() {
    if (text.value !== '' && !validTime(text.value)) {
        setText(toTime(props.value) || '')
    }
}

function onPick(time) {
    setText(toTime(time) || '')
    emit('input', toTime(time))
}
</script>
